import React, { useState, useEffect, useCallback } from "react";
import {
  Badge, IconButton, Popover, Typography, TextField, Button,
  List, ListItem, ListItemText, ListItemSecondaryAction, Divider,
  CircularProgress
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SvgIcon from "@material-ui/core/SvgIcon";

// Pencil/edit icon — Agregar nota al contacto
const PencilIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24" style={{ fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", ...props.style }}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </SvgIcon>
);
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import { format, parseISO } from "date-fns";
import { useContext } from "react";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  popoverPaper: {
    width: 450,
    maxHeight: 600,
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2),
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: theme.spacing(1.5),
    fontWeight: 700,
    fontSize: 15,
  },
  notesList: {
    flex: 1,
    overflowY: "auto",
    maxHeight: 260,
    marginBottom: theme.spacing(1),
  },
  noteText: {
    fontSize: 15,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    lineHeight: 1.6,
  },
  noteMeta: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  addArea: {
    paddingTop: theme.spacing(1.5),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  addRow: {
    display: "flex",
    gap: theme.spacing(1.5),
    alignItems: "flex-end",
  },
  empty: {
    padding: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
}));

const QuickNotesPopover = ({ contact }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchNotes = useCallback(async () => {
    if (!contact?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/contacts/${contact.id}/notes`);
      setNotes(data);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, [contact?.id]);

  useEffect(() => {
    if (anchorEl) fetchNotes();
  }, [anchorEl, fetchNotes]);

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setNewNote("");
  };

  const handleAdd = async () => {
    if (!newNote.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/contacts/${contact.id}/notes`, { note: newNote });
      setNotes((prev) => [data, ...prev]);
      setNewNote("");
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  const handleDelete = async (noteId) => {
    try {
      await api.delete(`/contacts/${contact.id}/notes/${noteId}`);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch (err) {
      toastError(err);
    }
  };

  if (!contact?.id) return null;

  return (
    <>
      <IconButton size="small" onClick={handleOpen} title="Agregar nota al contacto">
        <Badge badgeContent={notes.length || null} color="secondary" max={99}>
          <PencilIcon fontSize="small" />
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: classes.popoverPaper }}
      >
        <Typography className={classes.header} variant="subtitle2">
          Notas del contacto
          {loading && <CircularProgress size={14} />}
        </Typography>
        <div className={classes.notesList}>
          {!loading && notes.length === 0 && (
            <div className={classes.empty}>Sin notas</div>
          )}
          <List dense disablePadding>
            {notes.map((note) => (
              <React.Fragment key={note.id}>
                <ListItem alignItems="flex-start" dense>
                  <ListItemText
                    disableTypography
                    primary={
                      <Typography className={classes.noteText}>{note.note}</Typography>
                    }
                    secondary={
                      <Typography className={classes.noteMeta}>
                        {note.user?.name} &middot;{" "}
                        {format(parseISO(note.createdAt), "dd/MM/yyyy HH:mm")}
                      </Typography>
                    }
                  />
                  {(note.userId === user?.id || user?.profile === "admin") && (
                    <ListItemSecondaryAction>
                      <IconButton size="small" edge="end" onClick={() => handleDelete(note.id)}>
                        <DeleteIcon style={{ fontSize: 14 }} />
                      </IconButton>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))}
          </List>
        </div>

        <div className={classes.addArea}>
          <div className={classes.addRow}>
            <TextField
              multiline
              rows={6}
              fullWidth
              variant="outlined"
              placeholder="Escribir nota..."
              inputProps={{ style: { fontSize: 15, lineHeight: 1.6 } }}
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.ctrlKey) handleAdd();
              }}
            />
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              disabled={!newNote.trim() || saving}
              style={{ whiteSpace: "nowrap", minWidth: 0 }}
            >
              {saving ? <CircularProgress size={14} /> : "Add"}
            </Button>
          </div>
          <Typography variant="caption" color="textSecondary">
            Ctrl+Enter para agregar
          </Typography>
        </div>
      </Popover>
    </>
  );
};

export default QuickNotesPopover;
